<template>
  <div class="pdf-verify-container">
    <div v-if="loading" class="loading-wrapper">
      <div class="loading-spinner"></div>
      <p>加载中... {{ loadingProgress }}%</p>
    </div>
    <div v-else-if="error" class="error-wrapper">
      <p>{{ error }}</p>
      <button @click="retry" class="retry-btn">重试</button>
    </div>
    <div v-else class="pdf-viewer-wrapper">
      <div class="pdf-toolbar">
        <span class="page-info">共 {{ totalPages }} 页</span>
        <button @click="zoomIn">放大</button>
        <button @click="zoomOut">缩小</button>
        <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
      </div>
      <div class="pdf-canvas-container" ref="canvasContainer">
        <!-- 所有页面的 canvas 都会渲染在这里 -->
        <div class="pdf-pages-wrapper">
          <canvas v-for="pageNum in totalPages" :key="pageNum" :ref="`pdfCanvas${pageNum}`" class="pdf-page-canvas"></canvas>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PdfVerify',
  data () {
    // 检测是否为移动设备
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    return {
      loading: true,
      loadingProgress: 0,
      error: '',
      pdfUrl: '',
      pdfDoc: null,
      totalPages: 0,
      scale: isMobile ? 0.75 : 1.5, // 移动设备使用 75% 缩放
      isMobile: isMobile
    }
  },
  mounted () {
    this.initPdfView()
  },
  beforeDestroy () {
    if (this.pdfDoc) {
      this.pdfDoc.destroy()
    }
  },
  methods: {
    async initPdfView () {
      try {
        this.loading = true
        this.error = ''
        this.loadingProgress = 0

        // 从URL参数中获取n参数
        const nParam = this.$route.query.n || ''

        if (!nParam) {
          throw new Error('缺少PDF文件参数')
        }

        console.log('PDF参数:', nParam)
        console.log('当前环境:', process.env.NODE_ENV)
        console.log('设备类型:', this.isMobile ? '移动设备' : '桌面设备')

        // 文件存储在远程服务器 https://www.lmiscloud.com/tjmis/files/
        if (process.env.NODE_ENV === 'development') {
          this.pdfUrl = `/pdf-proxy/files/${nParam}.pdf`
          console.log('PDF URL (开发环境-代理到远程):', this.pdfUrl)
        } else {
          // this.pdfUrl = `/tjmis/files/${nParam}.pdf`
          this.pdfUrl = `/files/${nParam}.pdf`
          console.log('PDF URL (生产环境-同域访问):', this.pdfUrl)
        }

        // 加载 PDF.js 库
        if (!window.pdfjsLib) {
          console.log('开始加载 PDF.js 库...')
          await this.loadPdfJs()
          console.log('PDF.js 库加载完成')
        }

        // 配置 PDF.js worker
        const workerSrc = `${window.location.origin}/pdfjs/build/pdf.worker.js`
        window.pdfjsLib.GlobalWorkerOptions.workerSrc = workerSrc

        // 加载 PDF 文档
        console.log('开始加载 PDF 文档...')
        const loadingTask = window.pdfjsLib.getDocument({
          url: this.pdfUrl,
          withCredentials: false,
          httpHeaders: {},
          disableRange: false,
          disableStream: false
        })

        // 监听加载进度
        loadingTask.onProgress = (progress) => {
          if (progress.total > 0) {
            this.loadingProgress = Math.round((progress.loaded / progress.total) * 100)
          }
        }

        this.pdfDoc = await loadingTask.promise
        this.totalPages = this.pdfDoc.numPages
        console.log('PDF文档加载成功，总页数:', this.totalPages)

        // 先关闭 loading，让 DOM 渲染出来
        this.loading = false

        // 等待 Vue 更新 DOM
        await this.$nextTick()

        // 渲染所有页面
        await this.renderAllPages()
      } catch (err) {
        console.error('PDF加载失败详情:', err)
        let errorMsg = 'PDF加载失败'

        if (err.name === 'MissingPDFException') {
          errorMsg = 'PDF文件不存在或无法访问'
        } else if (err.name === 'InvalidPDFException') {
          errorMsg = 'PDF文件格式无效'
        } else if (err.name === 'UnexpectedResponseException') {
          errorMsg = 'PDF文件请求失败，请检查网络连接'
        } else if (err.message) {
          errorMsg = 'PDF加载失败: ' + err.message
        }

        this.error = errorMsg
        this.loading = false
      }
    },

    loadPdfJs () {
      return new Promise((resolve, reject) => {
        if (window.pdfjsLib) {
          resolve()
          return
        }

        const script = document.createElement('script')
        script.src = `${window.location.origin}/pdfjs/build/pdf.js`
        script.onload = () => {
          console.log('PDF.js 脚本加载成功')
          resolve()
        }
        script.onerror = (err) => {
          console.error('PDF.js 脚本加载失败:', err)
          reject(new Error('PDF.js库加载失败'))
        }
        document.head.appendChild(script)
      })
    },

    retry () {
      this.initPdfView()
    },

    async renderAllPages () {
      console.log('开始渲染所有页面...')

      // 渲染所有页面
      for (let pageNum = 1; pageNum <= this.totalPages; pageNum++) {
        await this.renderPage(pageNum)
      }

      console.log('所有页面渲染完成')
    },

    async renderPage (pageNum) {
      try {
        // 获取对应页面的 canvas 元素
        const canvasArray = this.$refs[`pdfCanvas${pageNum}`]
        if (!canvasArray || canvasArray.length === 0) {
          console.error(`Canvas ${pageNum} 元素未找到`)
          return
        }

        const canvas = canvasArray[0]
        const context = canvas.getContext('2d')

        // 获取页面
        const page = await this.pdfDoc.getPage(pageNum)

        // 获取设备像素比，支持高分辨率屏幕（Retina 屏）
        const devicePixelRatio = window.devicePixelRatio || 1

        // 计算实际渲染的 scale，考虑设备像素比
        const actualScale = this.scale * devicePixelRatio

        // 获取视口尺寸
        const viewport = page.getViewport({ scale: actualScale })

        // 设置 canvas 的实际像素尺寸（考虑设备像素比）
        canvas.height = viewport.height
        canvas.width = viewport.width

        // 设置 canvas 的 CSS 显示尺寸（不考虑设备像素比）
        canvas.style.height = `${viewport.height / devicePixelRatio}px`
        canvas.style.width = `${viewport.width / devicePixelRatio}px`

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        }

        await page.render(renderContext).promise
        console.log(`第 ${pageNum} 页渲染成功`)
      } catch (err) {
        console.error(`第 ${pageNum} 页渲染失败:`, err)
      }
    },

    async zoomIn () {
      this.scale += 0.25
      await this.renderAllPages()
    },

    async zoomOut () {
      if (this.scale <= 0.5) return
      this.scale -= 0.25
      await this.renderAllPages()
    }
  }
}
</script>

<style scoped>
.pdf-verify-container {
  width: 100%;
  height: 100vh;
  margin: 0;
  padding: 0;
  overflow: hidden;
}

.loading-wrapper,
.error-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  font-size: 16px;
  color: #606266;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #409eff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-wrapper p {
  color: #f56c6c;
  margin-bottom: 20px;
}

.retry-btn {
  padding: 10px 20px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.retry-btn:hover {
  background-color: #66b1ff;
}

.pdf-viewer-wrapper {
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
}

.pdf-toolbar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 10px;
  background-color: #f5f5f5;
  border-bottom: 1px solid #ddd;
  flex-shrink: 0;
}

.pdf-toolbar button {
  padding: 8px 16px;
  background-color: #409eff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.pdf-toolbar button:hover:not(:disabled) {
  background-color: #66b1ff;
}

.pdf-toolbar button:disabled {
  background-color: #c0c4cc;
  cursor: not-allowed;
}

.page-info,
.zoom-info {
  font-size: 14px;
  color: #606266;
}

.pdf-canvas-container {
  flex: 1;
  overflow: auto;
  background-color: #525659;
  -webkit-overflow-scrolling: touch; /* iOS 平滑滚动 */
}

.pdf-pages-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  gap: 10px; /* 页面之间的间距 */
}

.pdf-page-canvas {
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.3);
  background-color: white;
  display: block;
}
</style>
